import { v4 as uuid } from 'uuid'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { manualArticleInput } from 'requests/order'

import { OrderTypeId, ArticlesCheck, OrderFields } from 'types/order'
import { SelectItem } from 'types/selectLists'

import { addHttpError } from 'store/http-error/http-error.actions'
import { setInitialValuesOrderRead } from 'store/order/order.actions'
import { selectOrderRead } from 'store/order/order.selectors'

import { articleCodeMap, defaultRiskyArticlesBefore1997, defaultRiskyArticlesAfter1997 } from 'constants/article'
import { selectToken } from 'store/users/users.selectors'

export type Article = { group: string; year: string }
export type ArticleRow = { id: string; code: string; group: string; year: string }

export const yearIsValid = (y: string) => /^\d{4}$/.test(y) && +y >= new Date().getFullYear() - 100 && +y <= new Date().getFullYear()
const ArticleToFormItem = (article: Article): ArticleRow => ({ id: uuid(), code: '', group: article.group, year: article.year })
const FormItemToArticle = (item: ArticleRow): Article => ({ group: item.group, year: item.year })
const getEmptyRow = () => ({ id: uuid(), code: '', group: '', year: '' })

export const useArticlesCheck = () => {
  const dispatch = useDispatch()
  const token = useSelector(selectToken)
  const orderFields = useSelector(selectOrderRead)
  const articlesCheck: ArticlesCheck | null = useMemo(() => orderFields[OrderFields.INTERNAL_META]?.articlesCheck || null, [orderFields])
  const initialItems = articlesCheck ? [...articlesCheck?.encodedArticles?.map(ArticleToFormItem), getEmptyRow()] : [getEmptyRow()]
  const [items, setItems] = useState<ArticleRow[]>(initialItems)

  const [isLoading, setIsLoading] = useState(false)

  const needsDriverArticles = useMemo(() => orderFields[OrderFields.CUSTOMER_FEATURES]?.needsDriverArticles || false, [orderFields])
  const isVisible =
    needsDriverArticles || [OrderTypeId.EMPLOYEE, OrderTypeId.STATE_DRIVER].includes(String(orderFields[OrderFields.ORDER_TYPE]) as OrderTypeId)

  const options: SelectItem[] = useMemo(() => {
    const customerFeatures: Record<string, any> = (orderFields[OrderFields.CUSTOMER_FEATURES] as any) || {}
    const riskyBefore: number[] = customerFeatures.riskyCriminalCodesBefore1997 || defaultRiskyArticlesBefore1997
    const riskyAfter: number[] = customerFeatures.riskyCriminalCodesAfter1997 || defaultRiskyArticlesAfter1997

    return Array.from(new Set([...riskyAfter, ...riskyBefore])).map((article) => ({ id: String(article), name: String(article) }))
  }, [orderFields])

  const onChange = useCallback(async (updated: Partial<ArticleRow>, id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, ...updated, ...('code' in updated ? { group: updated.code ? articleCodeMap[updated.code] || 'Н?' : '' } : {}) }
          : item,
      ),
    )
  }, [])

  const handleRemove = useCallback(async (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const submitManual = useCallback(async () => {
    setIsLoading(true)
    try {
      const articles = items.map(FormItemToArticle).filter((item) => !!item.group)
      const response = await manualArticleInput(orderFields[OrderFields.ORDER_NUMBER], articles, token!)
      if (response)
        dispatch(
          setInitialValuesOrderRead({
            ...orderFields,
            [OrderFields.INTERNAL_META]: { ...orderFields[OrderFields.INTERNAL_META], articlesCheck: response },
          }),
        )
    } catch (error) {
      dispatch(addHttpError({ error, header: 'Сохранение статей', defaultText: 'Не удалось сохранить статьи.' }))
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, token, orderFields, items])

  useEffect(() => {
    if (!items.some((item) => !item.group)) setItems([...items, getEmptyRow()])
  }, [items])

  return {
    articlesCheck,
    items,
    options,
    onChange,
    handleRemove,
    isLoading,
    submitManual,
    isVisible,
  }
}
